import { PlansVariant } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import amountWithMinorCurrency from 'features/Onboarding/modules/PricingPlan/helpers/amountWithMinorCurrency';
import { OnboardingPlanDisplay } from 'features/Onboarding/modules/StepsPricingPlan/pages/PlanSelection/types';
import { ActiveProviderPlanId } from 'types/planUtils';

import locales from './locales';

import { ProConItem } from '.';

type BuildBulletPointsFn = (
  plan: Pick<
    OnboardingPlanDisplay,
    | 'providerPlanId'
    | 'features'
    | 'paymentCard'
    | 'limits'
    | 'customerSupport'
    | 'packageType'
  >,
  variant?: PlansVariant | null,
) => ProConItem[];

const bulletPointsBuilders: Record<ActiveProviderPlanId, BuildBulletPointsFn> =
  {
    basic: (plan) => {
      return [
        {
          text: locales.basicIntro,
        },
        {
          text: locales.formatString(locales.paymentCard, {
            count: plan.limits.physicalPaymentCard.value,
            type:
              plan.paymentCard.type === 'basicDebitCard'
                ? 'Mastercard Basic'
                : 'Mastercard Business World Debit',
          }),
        },
        {
          text: locales.formatString(locales.sepaTransferMonthBasic, {
            count: plan.limits.sepaTransferMonth.value,
          }),
        },
        {
          text: locales.basicFeatures,
        },
        {
          text: locales.basicSupport,
        },
      ];
    },
    business: (plan) => {
      return [
        {
          text: locales.businessIntro,
        },
        {
          text: locales.formatString(locales.proBusinessTeamMember, {
            cardCount: plan.limits.physicalPaymentCard.value,
            memberCountAccess: plan.limits.companyUserAccess.value,
            type:
              plan.paymentCard.type === 'basicDebitCard'
                ? 'Mastercard Basic'
                : 'Business World',
          }),
        },
        {
          text: locales.formatString(locales.sepaTransferMonthTeam, {
            count: plan.limits.sepaTransferMonth.value,
          }),
        },
        {
          isNewFeature: true,
          text: locales.proPlusBusinessTeamWork,
        },
        {
          isNewFeature: true,
          text: locales.formatString(locales.cashDeposit, {
            count: formatAmount({
              forcePlusSign: false,
              value: amountWithMinorCurrency(
                plan.limits.cashDepositMaxAmountPerMonth.value,
              ),
              withNoMinorUnitDigits: true,
            }),
          }),
        },
        {
          text: locales.formatString(locales.bankTeam, {
            count: plan.limits.checkDepositMonth.value,
          }),
        },
        {
          text: locales.prioritySupport,
        },
      ];
    },
    plus: (plan) => {
      return [
        {
          text: locales.plusIntro,
        },
        {
          text: locales.formatString(locales.paymentCard, {
            count: plan.limits.physicalPaymentCard.value,
            type:
              plan.paymentCard.type === 'basicDebitCard'
                ? 'Mastercard Basic'
                : 'Mastercard Business World Debit',
          }),
        },
        {
          text: locales.formatString(locales.sepaTransferMonthPlusSolo, {
            count: plan.limits.sepaTransferMonth.value,
          }),
        },
        {
          text: locales.formatString(locales.cashDeposit, {
            count: formatAmount({
              forcePlusSign: false,
              value:
                amountWithMinorCurrency(
                  plan.limits.cashDepositMaxAmountPerDeposit.value,
                ) * plan.limits.cashDepositMonth.value,
              withNoMinorUnitDigits: true,
            }),
          }),
        },
        {
          text: locales.formatString(locales.plusFeatures, {
            checkCount: plan.limits.checkDepositMonth.value,
            count: plan.limits.virtualPaymentCardMonth.value,
          }),
        },
        {
          text: locales.plusSupport,
        },
        {
          isNewFeature: true,
          text: locales.proAccountAccess,
        },
      ];
    },
    pro: (plan, variant) => {
      if (variant === PlansVariant.Solo || plan.packageType) {
        return [
          {
            text: locales.proIntro,
          },
          {
            text: locales.formatString(locales.sepaTransferMonthProSolo, {
              count: plan.limits.sepaTransferMonth.value,
            }),
          },
          {
            text: locales.formatString(locales.cashDeposit, {
              count: formatAmount({
                forcePlusSign: false,
                value:
                  amountWithMinorCurrency(
                    plan.limits.cashDepositMaxAmountPerDeposit.value,
                  ) * plan.limits.cashDepositMonth.value,
                withNoMinorUnitDigits: true,
              }),
            }),
          },
          {
            isNewFeature: true,
            text: locales.proAccountAccess,
          },
          {
            isNewFeature: true,
            text: locales.proPlusBusinessTeamWork,
          },
          {
            isNewFeature: true,
            text: locales.supplementaryInsurance,
          },
          {
            text: locales.prioritySupport,
          },
        ];
      }
      return [
        {
          text: locales.proIntroTeam,
        },
        {
          text: locales.formatString(locales.proBusinessTeamMember, {
            cardCount: plan.limits.physicalPaymentCard.value,
            memberCountAccess: plan.limits.companyUserAccess.value,
            type:
              plan.paymentCard.type === 'basicDebitCard'
                ? 'Mastercard Basic'
                : 'Business World',
          }),
        },
        {
          text: locales.formatString(locales.sepaTransferMonthTeam, {
            count: plan.limits.sepaTransferMonth.value,
          }),
        },
        {
          isNewFeature: true,
          text: locales.proPlusBusinessTeamWork,
        },
        {
          isNewFeature: true,
          text: locales.formatString(locales.cashDeposit, {
            count: formatAmount({
              forcePlusSign: false,
              value:
                amountWithMinorCurrency(
                  plan.limits.cashDepositMaxAmountPerDeposit.value,
                ) * plan.limits.cashDepositMonth.value,
              withNoMinorUnitDigits: true,
            }),
          }),
        },
        {
          text: locales.formatString(locales.bankTeam, {
            count: plan.limits.checkDepositMonth.value,
          }),
        },
        {
          text: locales.prioritySupport,
        },
      ];
    },
  };

export default bulletPointsBuilders;
